import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, signal } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { DefaultFrequencyLimitService } from '../../../../woo_services.module/DefaultFrequencyLimitService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { DefaultFrequencyLimit } from '../../../../woo_services.module/shared-types';

@Component({
  selector: 'manage-default-frequency-limits',
  templateUrl: './manage-default-frequency-limits.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageDefaultFrequencyLimits implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;
  show = {
    loading: true,
  };

  readonly staticFields = 4;
  readonly tableHeaders = ['Namn', 'Impressions', 'Tidsenhet', 'Redigera'];

  defaultFrequencyLimits = signal<DefaultFrequencyLimit[]>([]);
  selectedRow = signal<DefaultFrequencyLimit | null>(null);
  editingRow: string;
  translateTimeUnit: (args: any) => string;
  constructor(
    private defaultFrequencyLimitService: DefaultFrequencyLimitService,
    private modalService: ModalService,
    private translationService: TranslationService,
    private changeDetection: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.translateTimeUnit = this.translationService.convertFrequencyLimitTimeUnit;
    this.getAndSetFrequencyLimits().then(() => {
      this.show.loading = false;
      this.changeDetection.markForCheck();
    });
  }

  async getAndSetFrequencyLimits(): Promise<void> {
    return this.defaultFrequencyLimitService.getAll().then((defaultFrequencyLimits) => {
      this.defaultFrequencyLimits.set(defaultFrequencyLimits);
    });
  }

  setEditingRow(frequncyLimit: DefaultFrequencyLimit): void {
    this.editingRow = frequncyLimit.id;
    this.selectedRow.set({ ...frequncyLimit });
  }

  unsetEditingRow(): void {
    const sel = this.selectedRow();
    if (sel && this.editingRow) {
      this.defaultFrequencyLimits.update((limits) =>
        limits.map((limit) => (limit.id === this.editingRow ? { ...limit, impressions: sel.impressions } : limit)),
      );
    }
    this.selectedRow.set(null);
    this.editingRow = '';
    this.changeDetection.markForCheck();
  }

  isEditingRow(frequencyLimit: DefaultFrequencyLimit): boolean {
    return frequencyLimit.id === this.editingRow;
  }

  async save(frequencyLimit: DefaultFrequencyLimit): Promise<void> {
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then((response) => {
        if (response.confirm) {
          this.defaultFrequencyLimitService
            .updateDefaultFrequencyLimit(frequencyLimit)
            .then((updatedFrequencyLimit) => {
              this.defaultFrequencyLimits.update((limits) =>
                limits.map((frequencyLimit) =>
                  frequencyLimit.id === updatedFrequencyLimit.id ? updatedFrequencyLimit : frequencyLimit,
                ),
              );
            });
        } else {
          this.getAndSetFrequencyLimits();
        }
      })
      .catch((err) => {
        this.modalService.openError(
          'Ajdå, något gick snett. Kunde inte spara ändringar.',
          this.translationService.convertError(err.error.error),
        );
      })
      .finally(() => {
        this.unsetEditingRow();
        this.changeDetection.markForCheck();
      });
  }
}
